/**
 * AdminRoleToggle.jsx — segmented 3-way switch the admin uses to "view as"
 * a Reader or Writer (or stay in full Admin god-mode).
 *
 * Sits in the TopBar, visible ONLY when the real role is `admin`. Toggling
 * a mode flips a localStorage key (see utils/spaceContext.js) and dispatches
 * a `space-changed` event so the entire shell re-renders nav / banners.
 *
 * Color cues:
 *   admin  → purple
 *   reader → indigo
 *   writer → amber
 *
 * Also used by AppShell to render a thin top color band + corner badge so
 * the active mode is unmissable.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, BookOpen, PenLine } from 'lucide-react';
import { setAdminViewingAs, getAdminViewingAs } from '../utils/spaceContext';

const MODES = [
  { key: 'admin',  label: 'Admin',  icon: ShieldCheck, accent: 'purple', route: '/admin-dashboard' },
  { key: 'reader', label: 'Reader', icon: BookOpen,    accent: 'indigo', route: '/discover' },
  { key: 'writer', label: 'Scribe', icon: PenLine,     accent: 'amber',  route: '/writer-dashboard' },
];

const ACCENTS = {
  purple: { active: 'bg-purple-600 text-white shadow-purple-500/30', ring: 'ring-purple-400' },
  indigo: { active: 'bg-indigo-600 text-white shadow-indigo-500/30', ring: 'ring-indigo-400' },
  amber:  { active: 'bg-amber-500 text-slate-900 shadow-amber-500/30', ring: 'ring-amber-400' },
};

const AdminRoleToggle = ({ compact = false, noNavigate = false }) => {
  const navigate = useNavigate();
  // Re-render when the shell flips the localStorage key. We don't subscribe
  // here because AppShell already does — we just read on every render.
  const current = getAdminViewingAs();

  const handleClick = (mode) => {
    if (mode.key === current) {
      // Tap-the-active-mode shortcut: navigate to that role's home unless
      // the caller explicitly disabled navigation (e.g. on /profile, where
      // the toggle is meant to swap the visible Stats panel in-place).
      if (!noNavigate) navigate(mode.route);
      return;
    }
    setAdminViewingAs(mode.key);
    if (!noNavigate) navigate(mode.route);
  };

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-slate-900/70 border border-slate-700/70 p-0.5 shadow-inner"
      data-testid="admin-role-toggle"
      role="radiogroup"
      aria-label="Viewing as"
    >
      {MODES.map((m) => {
        const Icon = m.icon;
        const isActive = current === m.key;
        const accent = ACCENTS[m.accent];
        return (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => handleClick(m)}
            className={`inline-flex items-center gap-1.5 rounded-full transition-all text-xs font-bold tracking-tight ${
              compact ? 'px-2 py-1' : 'px-2.5 sm:px-3 py-1.5'
            } ${
              isActive
                ? `${accent.active} shadow-md`
                : 'text-slate-300 hover:text-white hover:bg-slate-800/80'
            }`}
            data-testid={`admin-role-toggle-${m.key}`}
          >
            <Icon className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} flex-shrink-0`} />
            <span className={compact ? 'hidden md:inline' : ''}>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default AdminRoleToggle;
